import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { Paginator } from '@/components/Paginator/Paginator';
import { NoResults } from '@/components/NoResults/NoResults';
import { Button } from '@/components/Button/Button';
import { Box } from '@/components/Box/Box';
import { Tabs } from '@/components/Tabs/Tabs';
import { Tab } from '@/components/Tabs/ui/Tab/Tab';
import NoItemsIcon from '@/images/NoItemsIcon.svg?react';
import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { OrganizationSubscriptionStatusBanner } from '@/bundle/shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { getListData, showSnackbarResponseError } from '@/helpers/helpers';
import { SecureLinkType, VaultAccountType, VaultRegionType } from '@/types/myVaultTypes';
import {
  loadMyVaultCatalog,
  loadSecureLinks,
  loadVaultAccounts,
  loadVaultRegions,
  MY_VAULT_QUERY_KEYS,
  updateVaultAccountStatus,
} from './api/api';
import { VaultAccountsList } from './ui/VaultAccountsList';
import { SecureLinksList } from './ui/SecureLinksList';
import { ShareSecureLinkModal } from './ui/ShareSecureLinkModal/ShareSecureLinkModal';
import { AddRailsModal } from './ui/AddRailsModal/AddRailsModal';
import { CloseVaultAccountModal } from './ui/CloseVaultAccountModal/CloseVaultAccountModal';
import { VerificationRecordModal } from './ui/VerificationRecordModal/VerificationRecordModal';
import { CLOSED_TAB } from './const/const';
import { SearchWrapper } from './styles';
import { CreateVaultAccountModal } from './ui/CreateVaultAccountModal/CreateVaultAccountModal';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { Typography } from '@/components/Typography/Typography';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import { useSearch } from '@/bundle/shared/hooks/useSearch';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { ConfirmVaultAccountModal } from './ui/ConfirmVaultAccountModal/ConfirmVaultAccountModal';

type ModalStateType =
  | { kind: 'share'; account: VaultAccountType; rail?: string }
  | { kind: 'addRails'; account: VaultAccountType; rail?: string }
  | { kind: 'close'; account: VaultAccountType }
  | { kind: 'verification'; link: SecureLinkType }
  | { kind: 'confirm'; account: VaultAccountType }
  | null;

type ParamsType = {
  tab?: string;
  q?: string;
  archived?: string;
  ordering?: string;
  create?: string;
};

const getSecureLinksHeader = (isArchived: boolean, q: string) => {
  let header = 'Secure links';

  if (isArchived) {
    header = 'Archived secure links';
  }

  if (q) {
    header += ` matching "${q}"`;
  }

  return header;
};

export const MyVaultPage = () => {
  const queryClient = useQueryClient();
  const { search, searchParams, submitSearch, clearSearch, setSearch } = useSearch<ParamsType>('q');
  const { replaceSearchParams } = useReplaceSearchParams<ParamsType>();
  const { isInactiveSubscription, isViewModeSubscription } = useSubscriptionStatus();
  const [modal, setModal] = useState<ModalStateType>(null);

  const [isOpenAccountModal, setIsOpenAccountModal] = useState(false);

  const { data: catalogResponse } = useQuery({
    queryKey: MY_VAULT_QUERY_KEYS.catalog,
    queryFn: loadMyVaultCatalog,
    staleTime: Infinity,
  });

  const { data: regionsResponse } = useQuery({ queryKey: MY_VAULT_QUERY_KEYS.regions, queryFn: loadVaultRegions });
  const catalog = catalogResponse?.body;
  const regions: VaultRegionType[] = regionsResponse?.body?.regions ?? [];
  const { ordering: _ordering, ...accountParams } = searchParams;

  const q = searchParams.q || '';
  const isArchived = searchParams.archived === '1';
  const tab = searchParams.tab || regions[0] || 'US';
  const isClosedTab = tab === CLOSED_TAB;

  const listParams = isClosedTab
    ? { ...accountParams, closed: '1' }
    : q
      ? { ...accountParams, q }
      : { ...accountParams, region: tab };
  const linkParams = {
    ...searchParams,
    ...(q ? { q } : { region: tab }),
    archived: isArchived ? '1' : undefined,
  };

  const { data: accountsResponse, isPending } = useQuery({
    queryKey: [MY_VAULT_QUERY_KEYS.accounts, listParams],
    queryFn: () => {
      return loadVaultAccounts(listParams);
    },
    placeholderData: keepPreviousData,
  });
  const { data: linksResponse, isPending: isLinksPending } = useQuery({
    queryKey: [MY_VAULT_QUERY_KEYS.links, linkParams],
    queryFn: () => loadSecureLinks(linkParams),
    enabled: !isClosedTab,
    placeholderData: keepPreviousData,
  });

  const { mutate: reopen } = useMutation({
    mutationKey: ['reopen_vault_account'],
    mutationFn: (id: string) => updateVaultAccountStatus(id, { status: 'pending_confirmation' }),
    onSuccess(response) {
      if (response?.error) return showSnackbarResponseError(response.error);

      queryClient.invalidateQueries({ queryKey: [MY_VAULT_QUERY_KEYS.accounts] });
      queryClient.invalidateQueries({ queryKey: MY_VAULT_QUERY_KEYS.regions });
    },
  });

  useEffect(() => {
    if (searchParams.create) {
      setIsOpenAccountModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { list, totalCount, hasData, hasPagination } = getListData(accountsResponse?.body, searchParams);
  const links = linksResponse?.body?.results ?? [];

  const changeTab = (value: string) => replaceSearchParams({ tab: value, page: null, q: null, archived: null });
  const closeModal = () => setModal(null);
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: [MY_VAULT_QUERY_KEYS.accounts] });
    queryClient.invalidateQueries({ queryKey: [MY_VAULT_QUERY_KEYS.links] });
    queryClient.invalidateQueries({ queryKey: MY_VAULT_QUERY_KEYS.regions });
  };

  const navigateToCreate = () => {
    setIsOpenAccountModal(true);
  };

  const closeAccountModal = () => {
    setIsOpenAccountModal(false);
    replaceSearchParams({ create: null });
  };

  const secureLinksHeader = getSecureLinksHeader(isArchived, q);

  return (
    <ContentLayout
      title='My Vault'
      breadcrumbs='My Vault'
      subtitle='Your receiving accounts, grouped by region. Share any account through a verified secure link.'
      headerAction={
        <Button disabled={isViewModeSubscription} width={170} mobileStretch onClick={navigateToCreate}>
          Add account
        </Button>
      }
      banner={isInactiveSubscription && <OrganizationSubscriptionStatusBanner />}
    >
      <PageBody>
        <Box marginBottom='24px'>
          <Tabs onChange={changeTab}>
            {(regions.length ? regions : (['US'] as VaultRegionType[])).map((region) => (
              <Tab key={region} value={region} isActive={tab === region}>
                {catalog?.regions[region]?.vault ?? region}
              </Tab>
            ))}
            <Tab value={CLOSED_TAB} isActive={isClosedTab}>
              Closed accounts
            </Tab>
          </Tabs>
        </Box>

        {!isClosedTab && (
          <SearchWrapper>
            <SearchInput
              name='my-vault-search'
              variant='small'
              hasErrorComponent={false}
              value={search}
              placeholder='Search by email, phone or last 4 digits of an account'
              aria-label='Search by email, phone or last 4 digits of an account'
              onClear={clearSearch}
              onChange={(value) => setSearch(value)}
              onSubmit={submitSearch}
            />
          </SearchWrapper>
        )}

        {hasData && (
          <VaultAccountsList
            list={list}
            onShare={(account) => setModal({ kind: 'share', account })}
            onAddRail={(account, rail) => setModal({ kind: 'addRails', account, rail })}
            onClose={(account) => setModal({ kind: 'close', account })}
            onReopen={(account) => reopen(account.id)}
            onConfirm={(account) => setModal({ kind: 'confirm', account })}
          />
        )}

        {!hasData && !isPending && !q && (
          <>
            <Typography variant='bodySmall'>
              Closed accounts can't be shared or used for insured transactions, ACH or invoices. Their records and
              history stay in the audit log.
            </Typography>

            <NoResults
              icon={<NoItemsIcon />}
              message={
                isClosedTab
                  ? 'No closed accounts.'
                  : 'Your vault is empty. Add the account you want payers to send money to.'
              }
            />
          </>
        )}

        {!isClosedTab && (
          <Box display='flex' flexDirection='column' marginTop='24px' gap='24px'>
            <Box marginLeft='auto'>
              <ButtonNew
                variant='secondary'
                size='medium'
                onClick={() => replaceSearchParams({ archived: isArchived ? undefined : '1', page: null })}
              >
                {isArchived ? 'Show active links' : 'Show archived links'}
              </ButtonNew>
            </Box>
            <SecureLinksList
              list={links}
              isLoading={isLinksPending}
              header={secureLinksHeader}
              onOpenVerification={(link) => setModal({ kind: 'verification', link })}
            />
          </Box>
        )}
      </PageBody>

      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}

      {modal?.kind === 'share' && catalog && (
        <ShareSecureLinkModal
          isOpen
          catalog={catalog}
          account={modal.account}
          initialRail={modal.rail}
          onClose={closeModal}
          onSuccess={invalidateAll}
        />
      )}
      {modal?.kind === 'addRails' && catalog && (
        <AddRailsModal
          isOpen
          catalog={catalog}
          account={modal.account}
          initialRail={modal.rail}
          onClose={closeModal}
          onSuccess={invalidateAll}
        />
      )}
      {modal?.kind === 'close' && (
        <CloseVaultAccountModal isOpen account={modal.account} onClose={closeModal} onSuccess={invalidateAll} />
      )}
      {modal?.kind === 'verification' && <VerificationRecordModal isOpen link={modal.link} onClose={closeModal} />}

      {modal?.kind === 'confirm' && (
        <ConfirmVaultAccountModal isOpen account={modal.account} onClose={closeModal} onSuccess={invalidateAll} />
      )}

      <Dialog isOpen={isOpenAccountModal} onClose={closeAccountModal} header='Set up My Vault' width='900px'>
        <CreateVaultAccountModal onClose={closeAccountModal} />
      </Dialog>
    </ContentLayout>
  );
};
